import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

import { CryptoService } from './crypto.service';

import {
  AuthResponse,
  LoginPayload,
  RegisterPayload,
  School,
  ChangePasswordPayload,
  ChangePasswordResponse,
  ApplicationData,
  GetApplicationsForActiveTermResponse,
  CreateApplicationPayload,
  CreateApplicationResponse,
  GetTimeslotsResponse,ResetPasswordPayload, SendCodeResponse, ResetPasswordResponse
} from '../models/auth.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = environment.apiUrl;
  private tokenKey = 'authToken';

  private jsonHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  });

  private acceptJsonHeaders = new HttpHeaders({
    'Accept': 'application/json'
  });

  private currentUserSchoolSubject: BehaviorSubject<School | null>;
  public currentUserSchool: Observable<School | null>;

  private activeApplicationsSubject: BehaviorSubject<ApplicationData[] | null>;
  public activeApplications$: Observable<ApplicationData[] | null>;

  constructor(private http: HttpClient, private cryptoService: CryptoService) {
    const storedSchoolEncrypted = localStorage.getItem('currentUserSchool');
    const storedSchool = this.cryptoService.decrypt<School>(storedSchoolEncrypted);
    this.currentUserSchoolSubject = new BehaviorSubject<School | null>(
      storedSchool
    );
    this.currentUserSchool = this.currentUserSchoolSubject.asObservable();
    
    const storedApplicationsEncrypted = localStorage.getItem('activeApplications');
    const storedApplications = this.cryptoService.decrypt<ApplicationData[]>(storedApplicationsEncrypted);
    this.activeApplicationsSubject = new BehaviorSubject<ApplicationData[] | null>(
      storedApplications
    );
    this.activeApplications$ = this.activeApplicationsSubject.asObservable();
  }

  public get currentUserSchoolValue(): School | null {
    return this.currentUserSchoolSubject.value;
  }

  public get activeApplicationsValue(): ApplicationData[] | null {
    return this.activeApplicationsSubject.value;
  }

  getToken(): string | null {
    const encryptedToken = localStorage.getItem(this.tokenKey);
    return this.cryptoService.decrypt<string>(encryptedToken);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  register(payload: RegisterPayload): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/register`, payload, { headers: this.jsonHeaders }).pipe(
      tap(response => {
        if (response.status === 'ok' && response.token && response.school) {
          localStorage.setItem(this.tokenKey, this.cryptoService.encrypt(response.token));
          localStorage.setItem('currentUserSchool', this.cryptoService.encrypt(response.school));
          this.currentUserSchoolSubject.next(response.school);
        } else {
          console.error('注册失败:', response.errors || response.message);
        }
      })
    );
  }

  login(payload: LoginPayload): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/login`, payload, { headers: this.jsonHeaders }).pipe(
      tap(response => {
        if (response.status === 'ok' && response.token && response.school) {
          localStorage.setItem(this.tokenKey, this.cryptoService.encrypt(response.token));
          localStorage.setItem('currentUserSchool', this.cryptoService.encrypt(response.school));
          this.currentUserSchoolSubject.next(response.school);
        } else {
          console.error('登录失败:', response.message || response.errors);
        }
      })
    );
  }

  logout(): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/logout`, {}, { headers: this.acceptJsonHeaders }).pipe(
      tap({
        next: (response) => {
          console.log('登出API调用成功:', response.message);
          this.clearAuthData();
        },
        error: (err) => {
          console.warn('登出API调用失败，但仍会清除本地登录状态:', err);
          this.clearAuthData();
        }
      })
    );
  }
  
  private clearAuthData(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem('currentUserSchool');
    localStorage.removeItem('activeApplications');
    this.currentUserSchoolSubject.next(null);
    this.activeApplicationsSubject.next(null);
    console.log('本地 Token、学校信息和申请列表已清除');
  }
  
  changePassword(payload: ChangePasswordPayload): Observable<ChangePasswordResponse> {
    return this.http.post<ChangePasswordResponse>(`${this.apiUrl}/changePassword`, payload, { headers: this.jsonHeaders });
  }

  getApplicationsForActiveTerm(): Observable<GetApplicationsForActiveTermResponse> {
    return this.http.get<GetApplicationsForActiveTermResponse>(`${this.apiUrl}/getApplicationForActiveTerm`, { headers: this.acceptJsonHeaders });
  }

  public setApplications(applications: ApplicationData[]): void {
    this.activeApplicationsSubject.next(applications);
    try {
      localStorage.setItem('activeApplications', this.cryptoService.encrypt(applications));
      console.log('Active applications list updated in service and localStorage (Encrypted).');
    } catch (e) {
      console.error('存储 applications 到 localStorage 失败:', e);
    }
  }


  createApplication(payload: CreateApplicationPayload): Observable<CreateApplicationResponse> {
    return this.http.post<CreateApplicationResponse>(`${this.apiUrl}/createApplication`, payload, {
      headers: this.jsonHeaders
    });
  }

  getTimeslotsByType(type: string): Observable<GetTimeslotsResponse> {
    const params = new HttpParams().set('type', type);
    return this.http.get<GetTimeslotsResponse>(`${this.apiUrl}/getTimeslotsByType/${type}`, {
      headers: this.acceptJsonHeaders,
      params: params
    });
  }
  sendPasswordResetCode(email: string): Observable<SendCodeResponse> {
    return this.http.post<SendCodeResponse>(
      `${this.apiUrl}/sendPasswordResetCode`, 
      { email }, 
      { headers: this.jsonHeaders } 
    );
  }

  resetPassword(payload: ResetPasswordPayload): Observable<ResetPasswordResponse> {
    return this.http.post<ResetPasswordResponse>(
      `${this.apiUrl}/resetPassword`, 
      payload, 
      { headers: this.jsonHeaders }
    );
  }
}